/**
 * Settings Manager
 *
 * Manages game settings and persists them to localStorage.
 * Handles movement type (gamma, beta, alpha), invert direction, and volume settings.
 */

export enum MovementType {
  GAMMA = 'gamma', // Roll - Left-right tilt (side edge tilt)
  BETA = 'beta', // Pitch - Forward-backward tilt (front-back tilt)
  ALPHA = 'alpha', // Yaw - Rotation around vertical axis (compass)
}

export interface GameSettings {
  movementType: MovementType;
  invertDirection: boolean;
  volumeSfx: number;
  volumeFall: number;
  volumeMusic: number;
  // Developer settings
  disableLeaderboardSubmission: boolean;
  showJsConsole: boolean;
  showPlatformIds: boolean;
  showFpsCounter: boolean;
  enableTeleport: boolean; // Enable teleport button for dev testing
}

export class SettingsManager {
  private static readonly STORAGE_KEY_MOVEMENT = 'blobo_movement_type';
  private static readonly STORAGE_KEY_INVERT = 'blobo_invert_direction';
  private static readonly STORAGE_KEY_VOLUME_SFX = 'blobo_volume_sfx';
  private static readonly STORAGE_KEY_VOLUME_FALL = 'blobo_volume_fall';
  private static readonly STORAGE_KEY_VOLUME_MUSIC = 'blobo_volume_music';
  private static readonly STORAGE_KEY_DISABLE_LEADERBOARD = 'blobo_dev_disable_leaderboard';
  private static readonly STORAGE_KEY_SHOW_JS_CONSOLE = 'blobo_dev_show_js_console';
  private static readonly STORAGE_KEY_SHOW_PLATFORM_IDS = 'blobo_dev_show_platform_ids';
  private static readonly STORAGE_KEY_SHOW_FPS_COUNTER = 'blobo_dev_show_fps_counter';
  private static readonly STORAGE_KEY_ENABLE_TELEPORT = 'blobo_dev_enable_teleport';
  private static readonly STORAGE_KEY_DEV_SETTINGS_VISIBLE = 'blobo_dev_settings_visible';

  // Default settings
  private static readonly DEFAULT_MOVEMENT_TYPE = MovementType.GAMMA;
  private static readonly DEFAULT_INVERT = false;
  private static readonly DEFAULT_VOLUME_SFX = 0.7; // 70% default for sound effects
  private static readonly DEFAULT_VOLUME_FALL = 0.7; // 70% default for fall sounds
  private static readonly DEFAULT_VOLUME_MUSIC = 0.5; // 50% default for music
  // Developer defaults
  private static readonly DEFAULT_DISABLE_LEADERBOARD = false;
  private static readonly DEFAULT_SHOW_JS_CONSOLE = false;
  private static readonly DEFAULT_SHOW_PLATFORM_IDS = false; // Hide platform IDs by default
  private static readonly DEFAULT_SHOW_FPS_COUNTER = false; // Hide FPS counter by default
  private static readonly DEFAULT_ENABLE_TELEPORT = false; // Disable teleport button by default
  private static readonly DEFAULT_DEV_SETTINGS_VISIBLE = false; // Hide developer settings by default

  /**
   * Get current movement type
   */
  public static getMovementType(): MovementType {
    const stored = localStorage.getItem(this.STORAGE_KEY_MOVEMENT);
    if (stored === MovementType.BETA) {
      return MovementType.BETA;
    }
    if (stored === MovementType.ALPHA) {
      return MovementType.ALPHA;
    }
    return this.DEFAULT_MOVEMENT_TYPE;
  }

  /**
   * Set movement type
   */
  public static setMovementType(type: MovementType): void {
    localStorage.setItem(this.STORAGE_KEY_MOVEMENT, type);
    console.log(`⚙️ Movement type set to: ${type}`);
  }

  /**
   * Get invert direction setting
   */
  public static getInvertDirection(): boolean {
    const stored = localStorage.getItem(this.STORAGE_KEY_INVERT);
    if (stored === null) {
      return this.DEFAULT_INVERT;
    }
    return stored === 'true';
  }

  /**
   * Set invert direction setting
   */
  public static setInvertDirection(invert: boolean): void {
    localStorage.setItem(this.STORAGE_KEY_INVERT, invert.toString());
    console.log(`⚙️ Invert direction set to: ${invert}`);
  }

  /**
   * Toggle invert direction
   */
  public static toggleInvertDirection(): boolean {
    const current = this.getInvertDirection();
    const newValue = !current;
    this.setInvertDirection(newValue);
    return newValue;
  }

  /**
   * Get SFX volume (0.0 to 1.0)
   */
  public static getVolumeSfx(): number {
    const stored = localStorage.getItem(this.STORAGE_KEY_VOLUME_SFX);
    if (stored === null) {
      return this.DEFAULT_VOLUME_SFX;
    }
    return parseFloat(stored);
  }

  /**
   * Set SFX volume (0.0 to 1.0)
   */
  public static setVolumeSfx(volume: number): void {
    volume = Math.max(0, Math.min(1, volume)); // Clamp 0-1
    localStorage.setItem(this.STORAGE_KEY_VOLUME_SFX, volume.toString());
  }

  /**
   * Get fall sounds volume (0.0 to 1.0)
   */
  public static getVolumeFall(): number {
    const stored = localStorage.getItem(this.STORAGE_KEY_VOLUME_FALL);
    if (stored === null) {
      return this.DEFAULT_VOLUME_FALL;
    }
    return parseFloat(stored);
  }

  /**
   * Set fall sounds volume (0.0 to 1.0)
   */
  public static setVolumeFall(volume: number): void {
    volume = Math.max(0, Math.min(1, volume)); // Clamp 0-1
    localStorage.setItem(this.STORAGE_KEY_VOLUME_FALL, volume.toString());
  }

  /**
   * Get music volume (0.0 to 1.0)
   */
  public static getVolumeMusic(): number {
    const stored = localStorage.getItem(this.STORAGE_KEY_VOLUME_MUSIC);
    if (stored === null) {
      return this.DEFAULT_VOLUME_MUSIC;
    }
    return parseFloat(stored);
  }

  /**
   * Set music volume (0.0 to 1.0)
   */
  public static setVolumeMusic(volume: number): void {
    volume = Math.max(0, Math.min(1, volume)); // Clamp 0-1
    localStorage.setItem(this.STORAGE_KEY_VOLUME_MUSIC, volume.toString());
  }

  /**
   * Get disable leaderboard submission setting (developer option)
   */
  public static getDisableLeaderboardSubmission(): boolean {
    const stored = localStorage.getItem(this.STORAGE_KEY_DISABLE_LEADERBOARD);
    if (stored === null) {
      return this.DEFAULT_DISABLE_LEADERBOARD;
    }
    return stored === 'true';
  }

  /**
   * Set disable leaderboard submission setting (developer option)
   */
  public static setDisableLeaderboardSubmission(disable: boolean): void {
    localStorage.setItem(this.STORAGE_KEY_DISABLE_LEADERBOARD, disable.toString());
    console.log(`⚙️ Disable leaderboard submission set to: ${disable}`);
  }

  /**
   * Toggle disable leaderboard submission
   */
  public static toggleDisableLeaderboardSubmission(): boolean {
    const current = this.getDisableLeaderboardSubmission();
    const newValue = !current;
    this.setDisableLeaderboardSubmission(newValue);
    return newValue;
  }

  /**
   * Get show JS console setting (developer option)
   */
  public static getShowJsConsole(): boolean {
    const stored = localStorage.getItem(this.STORAGE_KEY_SHOW_JS_CONSOLE);
    if (stored === null) {
      return this.DEFAULT_SHOW_JS_CONSOLE;
    }
    return stored === 'true';
  }

  /**
   * Set show JS console setting (developer option)
   */
  public static setShowJsConsole(show: boolean): void {
    localStorage.setItem(this.STORAGE_KEY_SHOW_JS_CONSOLE, show.toString());
    console.log(`⚙️ Show JS console set to: ${show}`);

    // Apply console visibility immediately
    this.applyConsoleVisibility(show);
  }

  /**
   * Toggle show JS console
   */
  public static toggleShowJsConsole(): boolean {
    const current = this.getShowJsConsole();
    const newValue = !current;
    this.setShowJsConsole(newValue);
    return newValue;
  }

  /**
   * Apply console visibility based on setting
   * Controls Eruda mobile console button visibility (not the panel itself)
   */
  private static applyConsoleVisibility(show: boolean): void {
    // Use the global function defined in index.html
    if (typeof (window as any).setErudaButtonVisibility === 'function') {
      (window as any).setErudaButtonVisibility(show);
    }
  }

  /**
   * Get show platform IDs setting (developer option)
   */
  public static getShowPlatformIds(): boolean {
    const stored = localStorage.getItem(this.STORAGE_KEY_SHOW_PLATFORM_IDS);
    if (stored === null) {
      return this.DEFAULT_SHOW_PLATFORM_IDS;
    }
    return stored === 'true';
  }

  /**
   * Set show platform IDs setting (developer option)
   */
  public static setShowPlatformIds(show: boolean): void {
    localStorage.setItem(this.STORAGE_KEY_SHOW_PLATFORM_IDS, show.toString());
    console.log(`⚙️ Show platform IDs set to: ${show}`);
  }

  /**
   * Toggle show platform IDs
   */
  public static toggleShowPlatformIds(): boolean {
    const current = this.getShowPlatformIds();
    const newValue = !current;
    this.setShowPlatformIds(newValue);
    return newValue;
  }

  /**
   * Get show FPS counter setting (developer option)
   */
  public static getShowFpsCounter(): boolean {
    const stored = localStorage.getItem(this.STORAGE_KEY_SHOW_FPS_COUNTER);
    if (stored === null) {
      return this.DEFAULT_SHOW_FPS_COUNTER;
    }
    return stored === 'true';
  }

  /**
   * Set show FPS counter setting (developer option)
   */
  public static setShowFpsCounter(show: boolean): void {
    localStorage.setItem(this.STORAGE_KEY_SHOW_FPS_COUNTER, show.toString());
    console.log(`⚙️ Show FPS counter set to: ${show}`);
  }

  /**
   * Toggle show FPS counter
   */
  public static toggleShowFpsCounter(): boolean {
    const current = this.getShowFpsCounter();
    const newValue = !current;
    this.setShowFpsCounter(newValue);
    return newValue;
  }

  /**
   * Get enable teleport setting (developer option)
   */
  public static getEnableTeleport(): boolean {
    const stored = localStorage.getItem(this.STORAGE_KEY_ENABLE_TELEPORT);
    if (stored === null) {
      return this.DEFAULT_ENABLE_TELEPORT;
    }
    return stored === 'true';
  }

  /**
   * Set enable teleport setting (developer option)
   */
  public static setEnableTeleport(enable: boolean): void {
    localStorage.setItem(this.STORAGE_KEY_ENABLE_TELEPORT, enable.toString());
    console.log(`⚙️ Enable teleport set to: ${enable}`);
  }

  /**
   * Toggle enable teleport
   */
  public static toggleEnableTeleport(): boolean {
    const current = this.getEnableTeleport();
    const newValue = !current;
    this.setEnableTeleport(newValue);
    return newValue;
  }

  /**
   * Get developer settings visibility (hidden by default)
   */
  public static getDevSettingsVisible(): boolean {
    const stored = localStorage.getItem(this.STORAGE_KEY_DEV_SETTINGS_VISIBLE);
    if (stored === null) {
      return this.DEFAULT_DEV_SETTINGS_VISIBLE;
    }
    return stored === 'true';
  }

  /**
   * Set developer settings visibility
   */
  public static setDevSettingsVisible(visible: boolean): void {
    localStorage.setItem(this.STORAGE_KEY_DEV_SETTINGS_VISIBLE, visible.toString());
    console.log(`⚙️ Developer settings visibility set to: ${visible}`);
  }

  /**
   * Toggle developer settings visibility
   * When enabled: auto-enable FPS counter, Disable Leaderboard, and Teleport
   * When disabled: auto-disable FPS counter, Disable Leaderboard, and Teleport
   */
  public static toggleDevSettingsVisible(): boolean {
    const current = this.getDevSettingsVisible();
    const newValue = !current;
    this.setDevSettingsVisible(newValue);

    // Auto-enable/disable related dev settings
    if (newValue) {
      // Dev mode ON: enable all dev features
      this.setShowFpsCounter(true);
      this.setDisableLeaderboardSubmission(true);
      this.setEnableTeleport(true);
      console.log('🔧 Dev mode enabled: FPS, Leaderboard disabled, Teleport enabled');
    } else {
      // Dev mode OFF: disable all dev features
      this.setShowFpsCounter(false);
      this.setDisableLeaderboardSubmission(false);
      this.setEnableTeleport(false);
      console.log('🔧 Dev mode disabled: FPS, Leaderboard, Teleport turned off');
    }

    return newValue;
  }

  /**
   * Get all settings
   */
  public static getSettings(): GameSettings {
    return {
      movementType: this.getMovementType(),
      invertDirection: this.getInvertDirection(),
      volumeSfx: this.getVolumeSfx(),
      volumeFall: this.getVolumeFall(),
      volumeMusic: this.getVolumeMusic(),
      disableLeaderboardSubmission: this.getDisableLeaderboardSubmission(),
      showJsConsole: this.getShowJsConsole(),
      showPlatformIds: this.getShowPlatformIds(),
      showFpsCounter: this.getShowFpsCounter(),
      enableTeleport: this.getEnableTeleport(),
    };
  }

  /**
   * Reset to default settings
   */
  public static resetToDefaults(): void {
    this.setMovementType(this.DEFAULT_MOVEMENT_TYPE);
    this.setInvertDirection(this.DEFAULT_INVERT);
    this.setVolumeSfx(this.DEFAULT_VOLUME_SFX);
    this.setVolumeFall(this.DEFAULT_VOLUME_FALL);
    this.setVolumeMusic(this.DEFAULT_VOLUME_MUSIC);
    this.setDisableLeaderboardSubmission(this.DEFAULT_DISABLE_LEADERBOARD);
    this.setShowJsConsole(this.DEFAULT_SHOW_JS_CONSOLE);
    this.setShowPlatformIds(this.DEFAULT_SHOW_PLATFORM_IDS);
    this.setShowFpsCounter(this.DEFAULT_SHOW_FPS_COUNTER);
    this.setEnableTeleport(this.DEFAULT_ENABLE_TELEPORT);
    console.log('⚙️ Settings reset to defaults');
  }
}
